// Presentation only. Memberships keep their own identity and term periods;
// the membership account used for invoices is never broadened to other accounts.
export function customerHubList(value) { return Array.isArray(value) ? value : value ? [value] : [] }

export function customerHubRenewalLabel(value) {
  return { AUTOMATIC: "Automatische Verlängerung", AUTO: "Automatische Verlängerung", MANUAL: "Manuelle Verlängerung", NONE: "Keine automatische Verlängerung" }[value] || "Verlängerung laut Vertrag"
}

export function membershipDate(value) {
  if (typeof value !== "string") return ""
  const day = value.slice(0, 10)
  if (day === "0001-01-01") return ""
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return ""
  const parsed = new Date(`${day}T12:00:00Z`)
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === day ? day : ""
}

function uniqueId(value) {
  const entry = customerHubList(value?.UniqueID)[0]
  return String(entry?.value || value?._id || (typeof value === "string" ? value : ""))
}

function stateCode(value) {
  return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value)) ? Number(value) : null
}

function membershipStatus(code, start, end, today, periods) {
  const group = code === 3 || code === 5 || (end && end < today) ? "past"
    : (start && start > today) || code === 2 ? "future" : "current"
  const paused = periods.some(period => period.type === "IDLEPERIOD" && period.start <= today && period.end >= today)
  const labels = { 0: "Provisorisch", 4: "Gekündigt", 5: "Beendet / storniert", 6: "Pausiert", 7: "Gesperrt" }
  const label = labels[code] || (group === "past" ? "Abgeschlossen" : group === "future" ? "Demnächst" : paused ? "Pausiert" : code === 1 || (start && end) ? "Aktiv" : "Status nicht angegeben")
  return { group, status: label, paused: paused || code === 6 }
}

function studioName(value) { return typeof value === "string" ? value : value?.name || value?.Name || "" }

function studioInformation(detail, studios, inheritedProperty = "") {
  const propertyId = uniqueId(detail.PropertyID) || inheritedProperty
  const studio = customerHubList(studios).find(item => String(item?.tacPropertyId || "") === propertyId)
  // PropertyID identifies the contract's studio, not a complete admission list.
  // Only display explicit studio availability when supplied with the record.
  const names = customerHubList(detail.availableInStudios).map(studioName).filter(Boolean)
  return {
    propertyId,
    contractStudio: studio?.name || detail.PropertyName || "Vertragsstudio nicht angegeben",
    studioValidity: names.length ? [...new Set(names)].join(", ") : "Studio-Gültigkeit nicht angegeben",
  }
}

function periodsFor(detail) {
  return customerHubList(detail.ContractTermTimes?.ContractTermTime).map((period, index) => ({
    id: `${period.Type || "period"}-${index}`,
    type: period.Type || "",
    label: period.Type === "IDLEPERIOD" ? "Pause" : period.Type === "BONUSPERIOD" ? "Bonusverlängerung" : "Vertragslaufzeit / Verlängerung",
    start: membershipDate(period.TimeSpan?.Start), end: membershipDate(period.TimeSpan?.End),
  })).filter(period => period.start || period.end).sort((a, b) => a.start.localeCompare(b.start))
}

function normalizeMembership(detail, index, today, studios, inheritedProperty = "") {
  const periods = periodsFor(detail)
  const usagePeriods = periods.filter(period => period.type !== "IDLEPERIOD")
  const starts = [membershipDate(detail.UsageStartDate), membershipDate(detail.ContractStartDate), ...usagePeriods.map(period => period.start)].filter(Boolean)
  const ends = [membershipDate(detail.UsageEndDate), membershipDate(detail.ContractEndDate), ...usagePeriods.map(period => period.end)].filter(Boolean)
  const start = starts.sort()[0] || ""
  let end = membershipDate(detail.UsageEndDate) || ends.sort().pop() || ""
  const terminationDate = membershipDate(detail.TerminationDate)
  if (terminationDate && (!end || terminationDate < end)) end = terminationDate
  const studio = studioInformation(detail, studios, inheritedProperty)
  const contractState = stateCode(detail.ContractState)
  return {
    id: uniqueId(detail.ContractID) || `membership-${index}`,
    name: detail.MemberCategoryName || "Mitgliedschaft",
    start, end, periods, terminationDate,
    renewal: typeof detail.RenewalMode === "string" ? detail.RenewalMode : "",
    terminationReason: typeof detail.TerminationInfo?.TerminationReason === "string" ? detail.TerminationInfo.TerminationReason : "",
    ...studio, ...membershipStatus(contractState, start, end, today, periods),
    packages: customerHubList(detail.SubContracts?.SubContract).map((entry, childIndex) => normalizeMembership(entry, `${index}-package-${childIndex}`, today, studios, studio.propertyId)),
  }
}

export function customerHubMemberships(response, { today, studios = [] } = {}) {
  const date = membershipDate(today)
  if (!date) throw new Error("A valid account-local calendar day is required")
  const memberships = customerHubList(response?.MemberDetails?.MemberDetail).map((detail, index) => normalizeMembership(detail, index, date, studios))
  const order = { current: 0, future: 1, past: 2 }
  return memberships.sort((left, right) => order[left.group] - order[right.group] ||
    (left.group === "past" ? right.end.localeCompare(left.end) : left.start.localeCompare(right.start)))
}

function timestamp(value) {
  const result = Date.parse(value)
  return Number.isFinite(result) ? result : 0
}

function invoiceDescription(value) {
  const labels = { "Mahngebühren 2 Umsatz": "2. Mahnung", "Mahngebühren 3 Umsatz": "3. Mahnung", "Mahngebühren": "3. Mahnung" }
  return labels[value] || value || "Mitgliedschaft"
}

export function customerHubInvoices(transactions, timestops = []) {
  // This is the same membership-account scope used by UserProfileContractCard.
  // PAYMENT settlement rows stay excluded; purchases/secondary accounts are not read.
  const rows = customerHubList(transactions?.membershipAccountTransactions)
    .filter(row => !(row.Type === "PAYMENT" && row.MainType === "PAYMENTS_AND_TRANSACTIONS"))
    .map((row, index) => {
      const details = customerHubList(row.AccountTransactionDetails?.AccountTransactionDetail)
      const items = details.map(detail => ({
        description: invoiceDescription(detail.Description),
        start: membershipDate(detail.TimeRange?.Start), end: membershipDate(detail.TimeRange?.End),
      }))
      const periods = items.filter(item => item.start || item.end).reduce((result, item) => {
        if (!result.some(period => period.start === item.start && period.end === item.end)) result.push({ start: item.start, end: item.end })
        return result
      }, [])
      const open = (row.Type === "OPEN_PAYMENT" && row.MainType === "RESULTING_PAYMENTS") || row.State === "OPEN"
      const paid = !open && row.State === "PAID"
      const value = row.Value?.value
      return {
        id: uniqueId(row.AccountTransactionID) || uniqueId(row.TransactionID) || `invoice-${index}`,
        title: details.length ? details.map(detail => invoiceDescription(detail.Description)).join(" · ") : invoiceDescription(row.Description),
        summaryTitle: items.length ? items[0].description : invoiceDescription(row.Description),
        items,
        open, paid, status: open ? "Offen" : paid ? "Bezahlt" : row.State === "CANCELLED" ? "Storniert" : "Verbuchter Eintrag", date: row.TransactionDate || "",
        dueDate: details.find(detail => detail.DueDate)?.DueDate || "",
        amount: value === null || value === undefined || value === "" || !Number.isFinite(Number(value)) ? null : Number(value),
        currency: row.Value?.currencyCode || "CHF", timestop: false,
        periods,
      }
    })
  // Pending Timestop fees already appear in the old open-payment view.
  customerHubList(timestops).filter(item => item.status === "onHold").forEach((item, index) => {
    const fee = item.tacData?.IdlePeriodFee?.value
    rows.push({
      id: `timestop-${item._id || index}`, title: "Timestop Gebühr", open: true, paid: false, status: "Offen", date: item.updatedAt || "", dueDate: "",
      amount: fee === null || fee === undefined || fee === "" || !Number.isFinite(Number(fee)) ? null : Number(fee),
      currency: "CHF", timestop: true, periods: [],
    })
  })
  return rows.sort((left, right) => Number(right.open) - Number(left.open) || (left.open
    ? timestamp(left.dueDate || left.date) - timestamp(right.dueDate || right.date)
    : timestamp(right.date) - timestamp(left.date)))
}
